import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  Button,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Save,
  CloudDownload,
  Delete,
  RestorePage,
  PlayArrow,
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  Storage,
  Wifi,
  InfoOutlined,
} from '@mui/icons-material';
import {
  getBackupSettings,
  updateBackupSettings,
  createBackup,
  getBackupHistory,
  downloadBackup,
  deleteBackupEntry,
  restoreBackup,
  restoreBackupFromFile,
  testSftpConnection,
} from '../api';
import { useConfirmDialog } from './ConfirmDialog';

export default function BackupSettings() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();

  const [settings, setSettings] = useState({
    scope: 'database_only',
    format: 'json',
    schedule_enabled: false,
    schedule_frequency: 'weekly',
    schedule_day_of_week: 0,
    schedule_day_of_month: 1,
    schedule_time: '02:00',
    retention_count: 5,
    sftp_enabled: false,
    sftp_host: '',
    sftp_port: 22,
    sftp_user: '',
    sftp_private_key_path: '',
    sftp_password: '',
    sftp_remote_path: '/backups',
  });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backing, setBacking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [restoreNotes, setRestoreNotes] = useState([]);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreConfirmation, setRestoreConfirmation] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadConfirmation, setUploadConfirmation] = useState('');
  const [testingSftp, setTestingSftp] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [settingsRes, historyRes] = await Promise.all([
        getBackupSettings(),
        getBackupHistory(),
      ]);
      if (settingsRes.data && Object.keys(settingsRes.data).length > 0) {
        setSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
      setHistory(historyRes.data.backups || []);
    } catch (err) {
      setError('Failed to load backup settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await updateBackupSettings(settings);
      setSettings(prev => ({ ...prev, ...res.data }));
      setSuccess('Backup settings saved');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateBackup = async () => {
    setBacking(true);
    setError('');
    setSuccess('');
    try {
      await createBackup({ scope: settings.scope, format: settings.format });
      setSuccess('Backup created successfully');
      // Reload history
      const historyRes = await getBackupHistory();
      setHistory(historyRes.data.backups || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Backup failed');
    } finally {
      setBacking(false);
    }
  };

  const handleDownload = async (backupId) => {
    try {
      await downloadBackup(backupId);
    } catch (err) {
      setError('Download failed');
    }
  };

  const handleDelete = async (backupId) => {
    const confirmed = await showConfirm({
      title: 'Delete Backup',
      message: 'Delete this backup? This cannot be undone.',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning'
    });
    if (!confirmed) return;
    try {
      await deleteBackupEntry(backupId);
      setHistory(prev => prev.filter(b => b.id !== backupId));
      setSuccess('Backup deleted');
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  // Follow-ups a restore leaves behind (credentials are never restored from a file). Non-zero items only.
  const buildRestoreNotes = (data) => {
    const notes = [];
    const users = data?.usersNeedingPasswordReset || 0;
    if (users > 0) notes.push(`${users} user account(s) were restored without a password. Reset each one from the People tab (Reset Password).`);
    const pas = data?.programAdminRowsSkipped || 0;
    if (pas > 0) notes.push(`${pas} program administrator account(s) in the file were ignored.`);
    const integrations = data?.integrationsNeedingReentry || [];
    if (integrations.includes('tenant_email_settings')) notes.push('Re-enter the email provider credentials in Email Settings.');
    if (integrations.includes('zoho_connections')) notes.push('Reconnect Zoho Books from the Zoho tab (Connect to Zoho).');
    const kiosks = data?.kioskDevicesNeedingRegistration || 0;
    if (kiosks > 0) notes.push(`${kiosks} kiosk device(s) must be registered again (Work Sites, Kiosk Devices tab).`);
    const auditInFile = data?.auditRowsInFile || 0;
    if (auditInFile > 0) {
      const notInLive = data?.auditRowsNotInLiveLog || 0;
      notes.push(`The audit log was not changed. The backup's ${auditInFile} audit entr${auditInFile === 1 ? 'y was' : 'ies were'} not loaded`
        + (notInLive > 0 ? ` (${notInLive} of them do not match the live audit log)` : '')
        + '. The restore itself was added to the audit log.');
    }
    return notes;
  };

  const handleRestore = async () => {
    if (restoreConfirmation !== 'RESTORE') return;
    setRestoring(true);
    setError('');
    try {
      const { data } = await restoreBackup(restoreTarget.id, 'RESTORE');
      setRestoreNotes(buildRestoreNotes(data));
      setSuccess('Backup restored successfully. Data has been replaced with the backup contents.');
      setRestoreDialogOpen(false);
      setRestoreConfirmation('');
      setRestoreTarget(null);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  const handleUploadRestore = async () => {
    if (uploadConfirmation !== 'RESTORE' || !uploadFile) return;
    setRestoring(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('backup', uploadFile);
      formData.append('confirmation', 'RESTORE');
      const { data } = await restoreBackupFromFile(formData);
      setRestoreNotes(buildRestoreNotes(data));
      setSuccess('Backup restored from uploaded file successfully.');
      setUploadDialogOpen(false);
      setUploadConfirmation('');
      setUploadFile(null);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Restore from file failed');
    } finally {
      setRestoring(false);
    }
  };

  const handleTestSftp = async () => {
    setTestingSftp(true);
    setError('');
    setSuccess('');
    try {
      const res = await testSftpConnection({
        sftp_host: settings.sftp_host,
        sftp_port: settings.sftp_port,
        sftp_user: settings.sftp_user,
        sftp_private_key_path: settings.sftp_private_key_path,
        sftp_password: settings.sftp_password,
        sftp_remote_path: settings.sftp_remote_path,
      });
      setSuccess(res.data.message || 'SFTP connection successful');
    } catch (err) {
      setError(err.response?.data?.error || 'SFTP connection failed');
    } finally {
      setTestingSftp(false);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ mb: 3 }}>Backup & Restore</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {restoreNotes.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setRestoreNotes([])}>
          After this restore:
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {restoreNotes.map((n) => <li key={n}>{n}</li>)}
          </Box>
        </Alert>
      )}

      {/* Backup Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Storage sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Backup Configuration</Typography>
        </Box>

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel>Backup Scope</FormLabel>
          <RadioGroup
            value={settings.scope}
            onChange={(e) => setSettings(prev => ({ ...prev, scope: e.target.value }))}
          >
            <FormControlLabel value="database_only" control={<Radio />} label="Database only" />
            <FormControlLabel value="database_files" control={<Radio />} label="Database + Files (pay stubs, tax filings)" />
            <FormControlLabel value="database_files_audit" control={<Radio />} label="Database + Files + Audit log" />
          </RadioGroup>
          <FormHelperText>The audit log copy is for download and archiving. A restore never changes or removes entries in the live audit log.</FormHelperText>
        </FormControl>

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel>Export Format</FormLabel>
          <RadioGroup
            value={settings.format}
            onChange={(e) => setSettings(prev => ({ ...prev, format: e.target.value }))}
            row
          >
            <FormControlLabel value="json" control={<Radio />} label="JSON" />
            <FormControlLabel value="sql" control={<Radio />} label="SQL" />
            <FormControlLabel value="both" control={<Radio />} label="Both" />
          </RadioGroup>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={backing ? <CircularProgress size={20} /> : <PlayArrow />}
            onClick={handleCreateBackup}
            disabled={backing}
          >
            {backing ? 'Creating...' : 'Create Backup Now'}
          </Button>
        </Box>
      </Paper>

      {/* Automated Schedule */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Schedule sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Automated Schedule</Typography>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={settings.schedule_enabled}
              onChange={(e) => setSettings(prev => ({ ...prev, schedule_enabled: e.target.checked }))}
            />
          }
          label="Enable automated backups"
          sx={{ mb: 2 }}
        />

        {settings.schedule_enabled && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', ml: 2 }}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Frequency</InputLabel>
              <Select
                value={settings.schedule_frequency}
                label="Frequency"
                onChange={(e) => setSettings(prev => ({ ...prev, schedule_frequency: e.target.value }))}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>

            {settings.schedule_frequency === 'weekly' && (
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Day of Week</InputLabel>
                <Select
                  value={settings.schedule_day_of_week}
                  label="Day of Week"
                  onChange={(e) => setSettings(prev => ({ ...prev, schedule_day_of_week: parseInt(e.target.value) }))}
                >
                  {dayNames.map((day, i) => (
                    <MenuItem key={i} value={i}>{day}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {settings.schedule_frequency === 'monthly' && (
              <TextField
                label="Day of Month"
                type="number"
                value={settings.schedule_day_of_month}
                onChange={(e) => setSettings(prev => ({ ...prev, schedule_day_of_month: parseInt(e.target.value) }))}
                inputProps={{ min: 1, max: 28 }}
                sx={{ width: 140 }}
              />
            )}

            <TextField
              label="Time"
              type="time"
              value={(settings.schedule_time || '02:00').substring(0, 5)}
              onChange={(e) => setSettings(prev => ({ ...prev, schedule_time: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 140 }}
            />

            <TextField
              label="Keep Last N Backups"
              type="number"
              value={settings.retention_count}
              onChange={(e) => setSettings(prev => ({ ...prev, retention_count: parseInt(e.target.value) || 5 }))}
              inputProps={{ min: 1, max: 100 }}
              sx={{ width: 170 }}
            />
          </Box>
        )}
      </Paper>

      {/* SFTP Remote Storage */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Wifi sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">SFTP Remote Storage</Typography>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={settings.sftp_enabled}
              onChange={(e) => setSettings(prev => ({ ...prev, sftp_enabled: e.target.checked }))}
            />
          }
          label="Push backups to SFTP server"
          sx={{ mb: 2 }}
        />

        {settings.sftp_enabled && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="SFTP Host"
                value={settings.sftp_host}
                onChange={(e) => setSettings(prev => ({ ...prev, sftp_host: e.target.value }))}
                sx={{ flex: 1, minWidth: 200 }}
              />
              <TextField
                label="Port"
                type="number"
                value={settings.sftp_port}
                onChange={(e) => setSettings(prev => ({ ...prev, sftp_port: parseInt(e.target.value) || 22 }))}
                sx={{ width: 100 }}
              />
            </Box>
            <TextField
              label="Username"
              value={settings.sftp_user}
              onChange={(e) => setSettings(prev => ({ ...prev, sftp_user: e.target.value }))}
            />
            <TextField
              label="Private Key Path (on server)"
              value={settings.sftp_private_key_path}
              onChange={(e) => setSettings(prev => ({ ...prev, sftp_private_key_path: e.target.value }))}
              helperText="Absolute path to SSH private key file on the HourTrax server"
            />
            <TextField
              label="Password (if no key)"
              type="password"
              value={settings.sftp_password}
              onChange={(e) => setSettings(prev => ({ ...prev, sftp_password: e.target.value }))}
              helperText="Encrypted at rest. Leave blank if using key authentication."
            />
            <TextField
              label="Remote Path"
              value={settings.sftp_remote_path}
              onChange={(e) => setSettings(prev => ({ ...prev, sftp_remote_path: e.target.value }))}
            />
            <Box>
              <Button
                variant="outlined"
                startIcon={testingSftp ? <CircularProgress size={20} /> : <Wifi />}
                onClick={handleTestSftp}
                disabled={testingSftp || !settings.sftp_host || !settings.sftp_user}
              >
                {testingSftp ? 'Testing...' : 'Test Connection'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Save Settings Button */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Backup History */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
          <Typography variant="h6">Backup History</Typography>
          <Tooltip title="Backups contain your organization's data but never passwords, MFA secrets, integration credentials or kiosk device tokens. Restoring keeps current passwords and credentials for accounts that still exist; accounts that only exist in the backup need a password reset.">
            <InfoOutlined fontSize="small" color="action" />
          </Tooltip>
        </Box>

        {history.length === 0 ? (
          <Typography color="text.secondary">No backups yet. Create your first backup above.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Trigger</TableCell>
                  <TableCell>SFTP</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((backup) => (
                  <TableRow key={backup.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(backup.created_at)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={backup.scope === 'database_only' ? 'DB' : backup.scope === 'database_files' ? 'DB+Files' : 'Full'}
                        color={backup.scope === 'database_files_audit' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{backup.format?.toUpperCase()}</TableCell>
                    <TableCell>{formatBytes(backup.size_bytes)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={backup.status === 'completed' ? <CheckCircle /> : backup.status === 'failed' ? <ErrorIcon /> : <Schedule />}
                        label={backup.status}
                        color={backup.status === 'completed' ? 'success' : backup.status === 'failed' ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>{backup.triggered_by}</TableCell>
                    <TableCell>{backup.sftp_pushed ? 'Yes' : '-'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {backup.status === 'completed' && (
                        <>
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={() => handleDownload(backup.id)}>
                              <CloudDownload fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Restore">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => {
                                setRestoreTarget(backup);
                                setRestoreDialogOpen(true);
                                setRestoreConfirmation('');
                              }}
                            >
                              <RestorePage fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(backup.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Restore from File */}
      <Paper sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'error.main' }}>
        <Typography variant="h6" color="error" sx={{ mb: 1 }}>Restore from File</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a previously exported backup file to restore. This will replace all current tenant data.
        </Typography>
        <Button
          variant="outlined"
          color="error"
          startIcon={<CloudUpload />}
          onClick={() => {
            setUploadDialogOpen(true);
            setUploadConfirmation('');
            setUploadFile(null);
          }}
        >
          Upload & Restore
        </Button>
      </Paper>

      {/* Restore Confirmation Dialog */}
      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Confirm Restore</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will DELETE all current tenant data and replace it with the backup from{' '}
            <strong>{restoreTarget && formatDate(restoreTarget.created_at)}</strong>.
            This action cannot be undone. Passwords, MFA and integration credentials are not changed by a restore. The audit log is kept as it is, and the restore is added to it.
          </Alert>
          {restoreTarget?.table_row_counts && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Backup contains: {Object.entries(restoreTarget.table_row_counts)
                .filter(([, count]) => count > 0)
                .map(([table, count]) => `${table} (${count})`)
                .join(', ')}
            </Typography>
          )}
          <TextField
            fullWidth
            label='Type "RESTORE" to confirm'
            value={restoreConfirmation}
            onChange={(e) => setRestoreConfirmation(e.target.value)}
            error={restoreConfirmation.length > 0 && restoreConfirmation !== 'RESTORE'}
            helperText={restoreConfirmation.length > 0 && restoreConfirmation !== 'RESTORE' ? 'Must type exactly "RESTORE"' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRestore}
            disabled={restoreConfirmation !== 'RESTORE' || restoring}
            startIcon={restoring ? <CircularProgress size={20} /> : <RestorePage />}
          >
            {restoring ? 'Restoring...' : 'Restore Backup'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Restore Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Restore from Uploaded File</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will DELETE all current tenant data and replace it with the uploaded backup.
            This action cannot be undone. Passwords, MFA and integration credentials are not changed by a restore. The audit log is kept as it is, and the restore is added to it.
          </Alert>
          <Button variant="outlined" component="label" sx={{ mb: 2 }}>
            Choose Backup File
            <input
              type="file"
              hidden
              accept=".gz"
              onChange={(e) => setUploadFile(e.target.files[0])}
            />
          </Button>
          {uploadFile && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Selected: {uploadFile.name} ({formatBytes(uploadFile.size)})
            </Typography>
          )}
          <TextField
            fullWidth
            label='Type "RESTORE" to confirm'
            value={uploadConfirmation}
            onChange={(e) => setUploadConfirmation(e.target.value)}
            error={uploadConfirmation.length > 0 && uploadConfirmation !== 'RESTORE'}
            helperText={uploadConfirmation.length > 0 && uploadConfirmation !== 'RESTORE' ? 'Must type exactly "RESTORE"' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleUploadRestore}
            disabled={uploadConfirmation !== 'RESTORE' || !uploadFile || restoring}
            startIcon={restoring ? <CircularProgress size={20} /> : <CloudUpload />}
          >
            {restoring ? 'Restoring...' : 'Upload & Restore'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialogComponent />
    </Box>
  );
}
